import { Button, Caption, Input, Label, Popover } from "@pej/kore/components";
import { type KeyboardEvent, type RefObject, useCallback, useEffect, useId, useRef, useState } from "react";
import type { NameResult } from "./asset-types";

export interface RenamePopoverProps {
    open: boolean;
    /** "Rename folder" or "Rename asset" — also the popover's accessible name. */
    title: string;
    initialValue: string;
    /** The control the rename was launched from; the popover anchors to it. */
    anchor: RefObject<HTMLElement | null>;
    onSubmit: (name: string) => Promise<NameResult>;
    onOpenChange: (open: boolean) => void;
}

/**
 * An anchored single-field rename (spec §5.5), prefilled and selected on open.
 *
 * The service's "already exists" verdict renders inline against the field
 * rather than as a toast: names are unique per workspace case-insensitively, so
 * a collision is an ordinary outcome, and the correction is a different name
 * typed into this very field. A toast would carry the message away from the
 * thing that has to change, and an announced-and-lost message is unreachable
 * for anyone who missed it.
 *
 * Anchored to the control that launched it so focus, attention and the surface
 * stay in one place — and so base UI restores focus there on close.
 */
export function RenamePopover({ open, title, initialValue, anchor, onSubmit, onOpenChange }: RenamePopoverProps) {
    const [value, setValue] = useState(initialValue);
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    // Key repeat can deliver a second Enter before the first commit's state
    // update lands, so state alone cannot hold this guard.
    const savingRef = useRef(false);
    // State, not a plain ref: the field lives inside `Popover.Content`, which
    // base-ui portals, so it can attach after this component's own commit —
    // a `useEffect` reading a plain ref's `.current` at that point would still
    // see `null`. Holding the node in state instead means its arrival is a
    // dependency the priming effect below can react to, whenever it lands.
    const [fieldNode, setFieldNode] = useState<HTMLInputElement | null>(null);
    const fieldRef = useCallback((node: HTMLInputElement | null) => setFieldNode(node), []);
    // Guards the one-time focus+select per open. `fieldNode` attaching and
    // `value` being reset to `initialValue` can each finish first depending on
    // portal timing, so both effects below can fire more than once per open —
    // this makes sure the actual focus()/select() call happens exactly once.
    const primedRef = useRef(false);
    const errorId = useId();

    // Re-prime on every open rather than on mount: the same popover instance is
    // reused for whatever is being renamed next, so a mount-only reset would
    // show the previous subject's name.
    useEffect(() => {
        if (!open) return;
        primedRef.current = false;
        setValue(initialValue);
        setError(null);
    }, [open, initialValue]);

    // Focus and select once the field is both attached *and* showing the
    // primed value — neither is guaranteed by the time the effect above runs.
    // Selecting before `value` has committed to the DOM would select the
    // previous subject's stale text instead of the current one, so this waits
    // on both `fieldNode` and `value` rather than firing from either alone.
    useEffect(() => {
        if (!open || primedRef.current || !fieldNode || value !== initialValue) return;
        // Selected, not just focused: the common edit is a full replacement, and
        // a caret parked at one end makes the operator clear the field first.
        fieldNode.focus();
        fieldNode.select();
        primedRef.current = true;
    }, [open, initialValue, value, fieldNode]);

    const submit = async () => {
        if (savingRef.current) return;
        const name = value.trim();
        if (name.length === 0) return;
        // Nothing to ask the service about, and asking would burn a round trip
        // to be told the name it already has is taken.
        if (name === initialValue) {
            onOpenChange(false);
            return;
        }
        savingRef.current = true;
        setSaving(true);
        setError(null);
        try {
            const result = await onSubmit(name);
            if (result.ok) onOpenChange(false);
            else setError(result.message);
        } finally {
            savingRef.current = false;
            setSaving(false);
        }
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key !== "Enter") return;
        event.preventDefault();
        void submit();
    };

    return (
        <Popover open={open} onOpenChange={onOpenChange}>
            <Popover.Content anchor={anchor} align="start" className="w-60">
                <Popover.Title>{title}</Popover.Title>
                <div className="mt-2 flex flex-col gap-2">
                    <Label htmlFor={`${errorId}-field`}>Name</Label>
                    <Input
                        id={`${errorId}-field`}
                        ref={fieldRef}
                        value={value}
                        aria-invalid={error !== null}
                        aria-describedby={error ? errorId : undefined}
                        onChange={(event) => setValue(event.target.value)}
                        onKeyDown={handleKeyDown}
                    />
                    {/* kore's `Caption` takes only `children`, `className` and `as` —
                        no `role`, and no `id`. Both live on a wrapper, and the
                        wrapper is what `aria-describedby` above points at, so the
                        association survives the indirection. */}
                    {error && (
                        <div id={errorId} role="alert">
                            <Caption className="text-kore-text-destructive">{error}</Caption>
                        </div>
                    )}
                    <div className="flex justify-end gap-2">
                        {/* Children live inside the render element, not beside it —
                            base UI's `render` replaces the part's own element, so a
                            self-closing render with sibling children drops them. */}
                        <Popover.Close
                            render={
                                <Button type="button" variant="outline" size="sm">
                                    Cancel
                                </Button>
                            }
                        />
                        <Button type="button" size="sm" disabled={saving} onClick={() => void submit()}>
                            {saving ? "Saving…" : "Save"}
                        </Button>
                    </div>
                </div>
            </Popover.Content>
        </Popover>
    );
}
